import { useEffect, useState } from "react";
import { Autocomplete, AutocompleteItem, Button, Tooltip } from "@heroui/react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

const SOCIAL_URL_FIELDS = ["url", "linkedin", "twitter", "x", "instagram", "tiktok", "youtube", "facebook"];

const pausedVerificationRegistry = new Map();

function toReferenceKey(referenceId) {
  const numeric = Number(referenceId);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  return `${referenceId}`;
}

function formatConfidence(candidate) {
  const totalScore = resolveCandidateTotalScore(candidate);
  if (!Number.isFinite(totalScore)) {
    return "score n/a";
  }
  return `score ${totalScore.toFixed(3)}`;
}

function resolveCandidateTotalScore(candidate) {
  const explicitTotal = Number(candidate?.total_score ?? candidate?.totalScore);
  if (Number.isFinite(explicitTotal)) {
    return explicitTotal;
  }
  const score = Number(candidate?.score ?? 0);
  const authenticityScore = Number(candidate?.authenticity?.score ?? candidate?.authenticityScore ?? 0);
  const searchScore = Number(candidate?.search_score ?? candidate?.searchScore ?? 0);
  const computed = score + authenticityScore + searchScore;
  return Number.isFinite(computed) ? computed : NaN;
}

function normalizeCandidates(rawCandidates = []) {
  if (!Array.isArray(rawCandidates)) {
    return [];
  }
  const deduped = new Map();
  rawCandidates.forEach((candidate) => {
    const url = `${candidate?.url ?? ""}`.trim();
    if (!url) {
      return;
    }
    const current = deduped.get(url);
    if (!current) {
      deduped.set(url, candidate);
      return;
    }
    const currentScore = Number.isFinite(resolveCandidateTotalScore(current))
      ? resolveCandidateTotalScore(current)
      : Number.NEGATIVE_INFINITY;
    const nextScore = Number.isFinite(resolveCandidateTotalScore(candidate))
      ? resolveCandidateTotalScore(candidate)
      : Number.NEGATIVE_INFINITY;
    if (nextScore > currentScore) {
      deduped.set(url, candidate);
    }
  });
  return Array.from(deduped.values()).sort((a, b) => {
    const aScore = Number.isFinite(resolveCandidateTotalScore(a))
      ? resolveCandidateTotalScore(a)
      : Number.NEGATIVE_INFINITY;
    const bScore = Number.isFinite(resolveCandidateTotalScore(b))
      ? resolveCandidateTotalScore(b)
      : Number.NEGATIVE_INFINITY;
    return bScore - aScore;
  });
}

function normalizeWebsiteCandidates(rawWebsites = []) {
  if (!Array.isArray(rawWebsites)) {
    return [];
  }
  const deduped = new Set();
  const items = [];
  rawWebsites.forEach((entry) => {
    const url = `${entry ?? ""}`.trim();
    if (!url || deduped.has(url)) {
      return;
    }
    deduped.add(url);
    items.push({
      key: `website_${url}`,
      url,
      confidence: "website option",
    });
  });
  return items;
}

function SocialUrlEditor({ person, field }) {
  const savedValue = `${person?.referenceParameters?.[field] ?? ""}`.trim();
  const [draftValue, setDraftValue] = useState(savedValue);
  const [isFocused, setIsFocused] = useState(false);
  const candidateItems = field === "url"
    ? normalizeWebsiteCandidates(person?.referenceParameters?.websites ?? [])
    : normalizeCandidates(
        person?.referenceParameters?.discovery_diagnostic?.social?.candidates?.[field] ?? [],
      ).map((candidate, idx) => {
        const url = `${candidate?.url ?? ""}`.trim();
        return {
          key: `${field}_candidate_${idx}_${url}`,
          url,
          confidence: formatConfidence(candidate),
        };
      });

  useEffect(() => {
    if (!isFocused) {
      setDraftValue(savedValue);
    }
  }, [savedValue, isFocused]);

  const commitValue = (nextRaw) => {
    const nextTrimmed = `${nextRaw ?? ""}`.trim();
    const normalizedNext = nextTrimmed.length > 0 ? nextTrimmed : null;
    const normalizedCurrent = savedValue.length > 0 ? savedValue : null;
    if (normalizedNext === normalizedCurrent) {
      return;
    }
    person.setField(`referenceParameters.${field}`, normalizedNext);
  };

  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
        {field}
      </span>
      <div className="flex items-center gap-2">
        <Autocomplete
          className="w-full"
          variant="bordered"
          size="sm"
          allowsCustomValue
          inputValue={draftValue}
          items={candidateItems}
          placeholder={field === "url" ? "Paste or pick website URL" : `Paste or pick ${field} URL`}
          onInputChange={(value) => {
            setDraftValue(`${value ?? ""}`);
          }}
          onSelectionChange={(key) => {
            const selected = candidateItems.find((item) => `${item.key}` === `${key}`);
            if (!selected) {
              return;
            }
            setDraftValue(selected.url);
            commitValue(selected.url);
          }}
          onFocus={() => {
            setIsFocused(true);
          }}
          onBlur={(event) => {
            setIsFocused(false);
            const rawValue = `${event?.target?.value ?? draftValue ?? ""}`;
            setDraftValue(rawValue);
            commitValue(rawValue);
          }}
          isClearable
          onClear={() => {
            setDraftValue("");
            commitValue("");
          }}
          listboxProps={{
            emptyContent: candidateItems.length > 0 ? undefined : "No candidate profiles",
          }}
        >
          {(item) => (
            <AutocompleteItem key={item.key} textValue={item.url}>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-xs">{item.url}</span>
                <div className="flex items-center gap-1">
                  <span className="shrink-0 text-[11px] text-slate-500">{item.confidence}</span>
                  <Tooltip content="Open candidate URL">
                    <span>
                      <Button
                        size="sm"
                        variant="light"
                        isIconOnly
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        onPress={(event) => {
                          event?.preventDefault?.();
                          event?.stopPropagation?.();
                          window.open(item.url, "_blank", "noopener,noreferrer");
                        }}
                      >
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              </div>
            </AutocompleteItem>
          )}
        </Autocomplete>
        <Tooltip content="Open link">
          <span>
            <Button
              size="sm"
              variant="light"
              isIconOnly
              isDisabled={!draftValue}
              onPress={() => {
                if (!draftValue) {
                  return;
                }
                window.open(draftValue, "_blank", "noopener,noreferrer");
              }}
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </Button>
          </span>
        </Tooltip>
      </div>
    </label>
  );
}

function ReferenceId44Verification({ people = [] }) {
  return (
    <div className="space-y-3">
      {people.map((person) => (
        <div key={person.id} className="rounded-lg border border-amber-200 bg-white p-3">
          <p className="text-sm font-semibold text-slate-800">
            {person.title ?? person.referenceParameters?.name ?? person.plainId ?? person.id}
          </p>
          <div className="mt-2 grid gap-2 md:grid-cols-2">
            {SOCIAL_URL_FIELDS.map((field) => (
              <SocialUrlEditor key={`${person.id}_${field}`} person={person} field={field} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function registerPausedVerificationViewer(referenceId, viewer) {
  const key = toReferenceKey(referenceId);
  if (!viewer || typeof viewer.collectItems !== "function" || typeof viewer.render !== "function") {
    return;
  }
  pausedVerificationRegistry.set(key, viewer);
}

export function collectPausedVerificationViews(stepPrimitive) {
  if (!stepPrimitive || (stepPrimitive.type !== "actionrunner" && stepPrimitive.type !== "action")) {
    return [];
  }
  const inputs = Array.isArray(stepPrimitive.itemsForProcessing) ? stepPrimitive.itemsForProcessing : [];
  const itemsByReference = new Map();
  inputs.forEach((item) => {
    if (!item?.id || item?.referenceId == null) {
      return;
    }
    const refKey = toReferenceKey(item.referenceId);
    if (!itemsByReference.has(refKey)) {
      itemsByReference.set(refKey, new Map());
    }
    const byId = itemsByReference.get(refKey);
    if (!byId.has(item.id)) {
      byId.set(item.id, item);
    }
  });

  return Array.from(itemsByReference.entries())
    .map(([referenceId, byId]) => {
      const viewer = pausedVerificationRegistry.get(referenceId);
      if (!viewer) {
        return null;
      }
      const items = viewer.collectItems(Array.from(byId.values()));
      if (!Array.isArray(items) || items.length === 0) {
        return null;
      }
      return {
        key: `paused_verification_${referenceId}`,
        title: viewer.title ?? "Manual verification",
        description: viewer.description ?? "",
        content: viewer.render(items),
      };
    })
    .filter(Boolean);
}

registerPausedVerificationViewer(44, {
  title: "Manual verification",
  description: "Verify and adjust social URLs before resuming this step.",
  collectItems(items = []) {
    return items.filter((item) => item?.id);
  },
  render(items = []) {
    return <ReferenceId44Verification people={items} />;
  },
});
